/**
 * Trading-key crypto for prediction-market TraderAuth.
 *
 * Signing goes through pm-trader-wasm (trader-wasm.js), a BLS-signing wasm
 * module loaded directly into this page - the secret key never leaves the
 * browser tab. Message construction (keccak domain) stays here so the wasm
 * module only signs an already-hashed message.
 *
 * Live testnet requires post-fork `sign()` (default). Pass `insecure: true`
 * only for VM::ephemeral-style local tests.
 */

import sha3 from "js-sha3";
import { generateKeypairWasm, deriveKeypairFromSeedWasm, signWasm } from "./trader-wasm.js";
import {
  TRADING_KEY_WALLET_MESSAGE,
  tradingKeyWalletMessageHex,
} from "./trading-key-wallet-message.js";

const { keccak256 } = sha3;

export { TRADING_KEY_WALLET_MESSAGE, tradingKeyWalletMessageHex };

export const OP = {
  DEPOSIT: 1,
  WITHDRAW: 2,
  TRADE: 3,
  REGISTER: 4,
  DISPUTE_YES: 5,
  CLAIM: 7,
  REDEEM: 8,
};

const DOMAIN_TRADER = "sme-platform.prediction-market.trader.v1";
const DOMAIN_COMMITMENT = "pm-trader-v1";
const DOMAIN_TRADING_KEY_SEED = "sme-platform.prediction-market.trading-key-seed.v1";
const DOMAIN_CLAIM = new TextEncoder().encode("ach-nft-claim-v1");

function hexToBytes(hex) {
  const h = String(hex).replace(/^0x/i, "");
  const out = new Uint8Array(h.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(h.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function bytesToHex(bytes) {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function concatBytes(parts) {
  const len = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(len);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

function u32Le(n) {
  const buf = new ArrayBuffer(4);
  new DataView(buf).setUint32(0, n >>> 0, true);
  return new Uint8Array(buf);
}

function u64Le(n) {
  const buf = new ArrayBuffer(8);
  new DataView(buf).setBigUint64(0, BigInt(n), true);
  return new Uint8Array(buf);
}

/**
 * Raw `claim_message` bytes for achievement-nft `claim()` - matches
 * `achievement_nft_types::auth::claim_message` (no keccak wrap).
 */
export function achievementClaimMessageBytes({ achievementType, value, epoch, rootBytes }) {
  if (!(rootBytes instanceof Uint8Array) || rootBytes.length !== 32) {
    throw new Error("rootBytes must be a 32-byte Uint8Array");
  }
  return concatBytes([
    DOMAIN_CLAIM,
    u32Le(achievementType),
    u64Le(value),
    u64Le(epoch),
    rootBytes,
  ]);
}

/** Hex of raw `claim_message` bytes (for `signWasm` on achievement claims). */
export function achievementClaimMessageHex({ achievementType, value, epoch, rootBytes }) {
  return bytesToHex(
    achievementClaimMessageBytes({ achievementType, value, epoch, rootBytes })
  );
}

/** BLS-sign raw claim bytes - not the PM trader keccak domain. */
export async function signAchievementClaim({
  skHex,
  achievementType,
  value,
  epoch,
  rootBytes,
  insecure = false,
}) {
  const messageHex = achievementClaimMessageHex({
    achievementType,
    value,
    epoch,
    rootBytes,
  });
  const { signature_hex } = await signWasm(skHex, messageHex, insecure);
  return { signatureHex: signature_hex, messageHex };
}

export function traderCommitmentFromPkHex(pkHex) {
  const pk = hexToBytes(pkHex);
  const domain = new TextEncoder().encode(DOMAIN_COMMITMENT);
  return keccak256(concatBytes([domain, pk]));
}

export function traderMessageHex(op, commitmentHex, nonce, payloadBytes) {
  const domain = new TextEncoder().encode(DOMAIN_TRADER);
  const commitment = hexToBytes(commitmentHex);
  const msg = concatBytes([
    domain,
    new Uint8Array([op]),
    commitment,
    u64Le(nonce),
    payloadBytes,
  ]);
  return keccak256(msg);
}

export function encodeDepositPayload(amount) {
  return u64Le(amount);
}

/**
 * `recipientBytes` (96-byte raw BLS pubkey) is bound into the signed
 * withdraw payload so the payout can't be redirected to whoever ends up
 * submitting the transaction - see `call_types::WithdrawArgs` doc
 * comment in the contract. Must match the `recipient` passed alongside
 * `auth` in the `withdraw_collateral` call args.
 */
export function encodeWithdrawPayload(amount, recipientBytes) {
  return concatBytes([u64Le(amount), recipientBytes]);
}

export function encodeTradePayload(marketId, outcome, isBuy, amount) {
  return concatBytes([
    u64Le(marketId),
    new Uint8Array([outcome & 0xff, isBuy ? 1 : 0]),
    u64Le(amount),
  ]);
}

export function encodeClaimPayload(marketId) {
  return u64Le(marketId);
}

export function encodeDisputeYesPayload(marketId, bond, evidenceHashBytes) {
  return concatBytes([u64Le(marketId), u64Le(bond), evidenceHashBytes]);
}

export async function generateTradingKey() {
  const { sk_hex, pk_hex } = await generateKeypairWasm();
  return {
    sk_hex,
    pk_hex,
    pk_base58: bytesToBase58(hexToBytes(pk_hex)),
    commitment_hex: traderCommitmentFromPkHex(pk_hex),
  };
}

/**
 * Derives a trading key from a wallet signature, instead of browser
 * randomness. `signatureHex` should be the raw signature bytes (hex, no
 * `0x`) returned by the wallet for `TRADING_KEY_WALLET_MESSAGE` -
 * deterministic given the same wallet, so this always reproduces the same
 * key. Never touches local storage itself; caller decides what (if
 * anything) to cache.
 */
export async function deriveTradingKeyFromSignatureHex(signatureHex) {
  const domain = new TextEncoder().encode(DOMAIN_TRADING_KEY_SEED);
  const seedHex = keccak256(concatBytes([domain, hexToBytes(signatureHex)]));
  const { sk_hex, pk_hex } = await deriveKeypairFromSeedWasm(seedHex);
  return {
    sk_hex,
    pk_hex,
    pk_base58: bytesToBase58(hexToBytes(pk_hex)),
    commitment_hex: traderCommitmentFromPkHex(pk_hex),
  };
}

/**
 * Prompts the connected wallet to sign `TRADING_KEY_WALLET_MESSAGE` and
 * derives the trading key from that signature. Requires a wallet that
 * supports `dusk_signMessage` (`wallet.signMessage`).
 *
 * Message is sent as `0x`-hex, not `Uint8Array`: the extension bridge
 * JSON-serializes RPC params, so TypedArrays arrive as plain objects and
 * the wallet rejects them with `params.message must be bytes…`. Same
 * pattern `@dusk/connect` already uses for contract-call `fnArgs`.
 */
export async function deriveTradingKeyFromWallet(wallet) {
  if (typeof wallet?.signMessage !== "function") {
    throw new Error("Connected wallet does not support message signing (dusk_signMessage)");
  }
  const messageHex = tradingKeyWalletMessageHex();
  const { signature } = await wallet.signMessage(messageHex);
  if (!signature) throw new Error("Wallet did not return a signature");
  const signatureHex = String(signature).replace(/^0x/i, "").toLowerCase();
  return deriveTradingKeyFromSignatureHex(signatureHex);
}

export async function signTraderAuth({ skHex, op, commitmentHex, nonce, payloadBytes, insecure = false }) {
  const messageHex = traderMessageHex(op, commitmentHex, nonce, payloadBytes);
  const { signature_hex } = await signWasm(skHex, messageHex, insecure);
  return {
    signatureHex: signature_hex,
    messageHex,
  };
}

const BASE58_ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

/** Encode raw bytes as Bitcoin-style base58 (matches bls12_381-bls serde). */
export function bytesToBase58(bytes) {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let digits = [0];
  for (const byte of arr) {
    let carry = byte;
    for (let i = 0; i < digits.length; i++) {
      carry += digits[i] << 8;
      digits[i] = carry % 58;
      carry = (carry / 58) | 0;
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = (carry / 58) | 0;
    }
  }
  let zeros = 0;
  while (zeros < arr.length && arr[zeros] === 0) zeros++;
  let out = "";
  for (let i = 0; i < zeros; i++) out += BASE58_ALPHABET[0];
  for (let i = digits.length - 1; i >= 0; i--) out += BASE58_ALPHABET[digits[i]];
  return out;
}

/** Inverse of `bytesToBase58`. Needed to bind a wallet's base58 account
 * id into a signed payload (see `encodeWithdrawPayload`). */
export function base58ToBytes(str) {
  let bytes = [0];
  for (const ch of str) {
    const value = BASE58_ALPHABET.indexOf(ch);
    if (value === -1) throw new Error(`invalid base58 character: ${ch}`);
    let carry = value;
    for (let i = 0; i < bytes.length; i++) {
      carry += bytes[i] * 58;
      bytes[i] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }
  let zeros = 0;
  while (zeros < str.length && str[zeros] === BASE58_ALPHABET[0]) zeros++;
  const out = new Uint8Array(zeros + bytes.length);
  out.set(bytes.reverse(), zeros);
  return out;
}

/** Build TraderAuth for data-driver JSON (BlsPublicKey + BlsSignature are both bs58). */
export function buildTraderAuthArg({ pkBase58, nonce, signatureHex }) {
  return {
    trading_pk: pkBase58,
    nonce,
    signature: bytesToBase58(hexToBytes(signatureHex)),
  };
}

export { bytesToHex, hexToBytes };
